/**
 * ArcadeClient — offline-first client for the shared arcade-cloud backend.
 *
 * Works with no account (localStorage). When the player signs in with a
 * username/password, saves and scores sync to Cloudflare (D1) and follow them
 * across devices. Never blocks gameplay on the network.
 */

export interface ArcadeUser {
  id: string;
  username: string;
}

export interface CloudSave<T = unknown> {
  data: T;
  version: number;
  updatedAt: number;
}

export interface LeaderboardEntry {
  rank: number;
  username: string;
  score: number;
  meta: unknown;
  createdAt: number;
}

export interface ArcadeClientOptions {
  /** Backend base URL. */
  baseUrl?: string;
  /** Stable game id used to namespace saves + scores (e.g. "deep-sea"). */
  gameId: string;
  /** localStorage key prefix. Defaults to `arcade:{gameId}`. */
  storagePrefix?: string;
}

const DEFAULT_BASE = 'https://arcade-cloud.bzllbrown.workers.dev';

export class ArcadeError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message?: string) {
    super(message || code);
    this.code = code;
    this.status = status;
  }
}

export class ArcadeClient {
  readonly baseUrl: string;
  readonly gameId: string;
  private readonly prefix: string;
  private token: string | null = null;
  private user: ArcadeUser | null = null;
  private listeners = new Set<(u: ArcadeUser | null) => void>();

  constructor(opts: ArcadeClientOptions) {
    this.baseUrl = (opts.baseUrl || DEFAULT_BASE).replace(/\/$/, '');
    this.gameId = opts.gameId;
    this.prefix = opts.storagePrefix || `arcade:${opts.gameId}`;
    this.token = this.ls(`${this.prefix}:token`);
    const cachedUser = this.ls(`${this.prefix}:user`);
    if (cachedUser) {
      try {
        this.user = JSON.parse(cachedUser);
      } catch {
        this.user = null;
      }
    }
  }

  /* ------------------------------- storage ------------------------------- */

  private ls(key: string): string | null {
    try {
      return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  }
  private setLs(key: string, value: string | null) {
    try {
      if (typeof localStorage === 'undefined') return;
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      /* private mode / quota — ignore */
    }
  }

  /* ------------------------------- auth ---------------------------------- */

  get currentUser(): ArcadeUser | null {
    return this.user;
  }
  get isSignedIn(): boolean {
    return !!this.token && !!this.user;
  }

  onAuthChange(fn: (u: ArcadeUser | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emitAuth() {
    for (const fn of this.listeners) fn(this.user);
  }

  private setSession(token: string, user: ArcadeUser) {
    this.token = token;
    this.user = user;
    this.setLs(`${this.prefix}:token`, token);
    this.setLs(`${this.prefix}:user`, JSON.stringify(user));
    this.emitAuth();
  }

  async register(username: string, password: string): Promise<ArcadeUser> {
    const res = await this.request('/auth/register', 'POST', { username, password });
    this.setSession(res.token, res.user);
    return res.user;
  }

  async login(username: string, password: string): Promise<ArcadeUser> {
    const res = await this.request('/auth/login', 'POST', { username, password });
    this.setSession(res.token, res.user);
    return res.user;
  }

  logout() {
    this.token = null;
    this.user = null;
    this.setLs(`${this.prefix}:token`, null);
    this.setLs(`${this.prefix}:user`, null);
    this.emitAuth();
  }

  /** Re-validate the stored token against the server; clears it if expired. */
  async refresh(): Promise<ArcadeUser | null> {
    if (!this.token) return null;
    try {
      const res = await this.request('/auth/me', 'GET');
      this.user = res.user;
      this.setLs(`${this.prefix}:user`, JSON.stringify(res.user));
      this.emitAuth();
      return res.user;
    } catch (e) {
      if (e instanceof ArcadeError && e.status === 401) this.logout();
      return null;
    }
  }

  /* --------------------------- saves (hybrid) ---------------------------- */

  private localSaveKey(slot: string) {
    return `${this.prefix}:save:${slot}`;
  }

  /** Read the local save immediately (synchronous, always available). */
  readLocal<T = unknown>(slot = 'main'): T | null {
    const raw = this.ls(this.localSaveKey(slot));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  /** Write locally now; push to cloud in the background if signed in. */
  save<T = unknown>(data: T, slot = 'main'): void {
    this.setLs(this.localSaveKey(slot), JSON.stringify(data));
    if (this.isSignedIn) {
      void this.request(`/saves/${encodeURIComponent(this.gameId)}?slot=${encodeURIComponent(slot)}`, 'PUT', {
        data,
      }).catch(() => {
        /* offline — local copy already persisted, will resync on next save */
      });
    }
  }

  /** Pull the cloud save if newer than local; returns the effective save. */
  async loadSave<T = unknown>(slot = 'main'): Promise<T | null> {
    const local = this.readLocal<T>(slot);
    if (!this.isSignedIn) return local;
    try {
      const res = await this.request(`/saves/${encodeURIComponent(this.gameId)}?slot=${encodeURIComponent(slot)}`, 'GET');
      const cloud = res.save as CloudSave<T> | null;
      if (cloud) {
        this.setLs(this.localSaveKey(slot), JSON.stringify(cloud.data));
        return cloud.data;
      }
      if (local) this.save(local, slot); // seed cloud from local
      return local;
    } catch {
      return local;
    }
  }

  /* ------------------------------ scores --------------------------------- */

  async submitScore(score: number, meta?: unknown): Promise<{ rank: number | null } | null> {
    if (!this.isSignedIn) return null;
    try {
      const res = await this.request(`/leaderboard/${encodeURIComponent(this.gameId)}`, 'POST', { score, meta });
      return { rank: res.rank ?? null };
    } catch {
      return null;
    }
  }

  async leaderboard(limit = 20): Promise<LeaderboardEntry[]> {
    try {
      const res = await this.request(
        `/leaderboard/${encodeURIComponent(this.gameId)}?limit=${limit}`,
        'GET',
      );
      return res.entries as LeaderboardEntry[];
    } catch {
      return [];
    }
  }

  /* ------------------------------- http ---------------------------------- */

  private async request(path: string, method: string, body?: unknown): Promise<any> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      /* non-JSON */
    }
    if (!res.ok) {
      throw new ArcadeError(json?.error || 'request_failed', res.status, json?.message);
    }
    return json;
  }
}
